import { unref, type Ref } from 'vue'
import type { MaybeRef } from '@vueuse/core'
import type { Instance } from 'pspdfkit'

import { requestFullscreen, exitFullscreen, isFullscreen, type FullscreenElement } from '@lib/util/fullscreen.util'
import type CFileViewerBottomPdf from '@lib/components/CFileViewer/pdf/CFileViewerBottom.pdf.vue'

export default ({
  pdfViewerEl,
  bottomBarRef,
  instance,
  isThumbnailsOpen,
  isSearchOpen,
}: {
  pdfViewerEl: MaybeRef<HTMLElement | null>
  bottomBarRef: Ref<InstanceType<typeof CFileViewerBottomPdf> | null>
  instance: MaybeRef<Instance | undefined>
  isThumbnailsOpen: Ref<boolean>
  isSearchOpen: Ref<boolean>
}) => {
  const registerFullscreenChangeEvents = (callback: (event: Event) => void) => {
    document.addEventListener('fullscreenchange', callback)
    document.addEventListener('webkitfullscreenchange', callback)
    document.addEventListener('mozfullscreenchange', callback)
    document.addEventListener('MSFullscreenChange', callback)
  }
  const removeFullscreenChangeEvents = (callback: (event: Event) => void) => {
    document.removeEventListener('fullscreenchange', callback)
    document.removeEventListener('webkitfullscreenchange', callback)
    document.removeEventListener('mozfullscreenchange', callback)
    document.removeEventListener('MSFullscreenChange', callback)
  }

  const requestFullScreen = async () => {
    const element = unref(pdfViewerEl) as HTMLElement | null
    const bottomBarEl = bottomBarRef.value?.$el

    function appendBottomBarToFullscreenElement() {
      if (bottomBarEl && element) {
        element.appendChild(bottomBarEl)
      }
    }

    const el = element as FullscreenElement | null

    if (!el) return

    await requestFullscreen(el)

    appendBottomBarToFullscreenElement()
  }

  const hideSidebar = () => {
    unref(instance)?.setViewState((viewState) => viewState.set('sidebarMode', null))
    isThumbnailsOpen.value = false
  }

  const hideSearch = () => {
    const i = unref(instance)
    i?.setViewState((viewState) => viewState.set('interactionMode', null))
    isSearchOpen.value = false
  }

  const focus = () => {
    const pspdfkitRoot = (document.querySelector('.PSPDFKit-Container')?.shadowRoot?.querySelector('.PSPDFKit-Root') ??
      null) as HTMLElement | null

    pspdfkitRoot?.focus()
  }

  const onFullscreen = async (forceEnter?: boolean) => {
    if (!forceEnter && isFullscreen()) {
      await exitFullscreen()
    } else {
      hideSidebar()
      hideSearch()
      await requestFullScreen()
      focus()
    }
  }

  return { registerFullscreenChangeEvents, removeFullscreenChangeEvents, onFullscreen }
}
